import { useField, useFormikContext } from 'formik';
import React, { useEffect, useReducer, useRef, useState } from 'react';

import styled from '@emotion/styled';

import useOutsideClick from '../../hooks/ui-control/useOutsideClick';
import { FieldErrorInfo, Input, InputLabel, InputWrapper } from './FormField';

interface CustomSelectSearchAsyncFieldProps {
  label?: any;
  id: string;
  name: string;
  placeholder?: string;
  className?: string;
  readonly?: boolean;
  onChange?: (value: string) => void;
  onSelect?: (data: Record<string, any>) => void;
  renderComponent?: (...args: any) => React.ReactNode;
  fetchDataFn?: (...args: any) => void;
  fetchDataLoading?: boolean;
  fetchedData?: any[];
  requiredDataKey?: string;
  presentationDataKey?: string;
}

// Type definition
export enum OptionReducerActionType {
  CLICKED_OPTION = 'option-clicked',
}

interface IOptionReducerActionType {
  type: OptionReducerActionType;
  payload: { index: number };
}

// Reducers
function selectOptionsReducer(
  state: { index: number },
  action: IOptionReducerActionType,
) {
  switch (action.type) {
    case OptionReducerActionType.CLICKED_OPTION:
      return { ...action.payload };
    default:
      return state;
  }
}

export const CustomSelectSearchAsyncField: React.FC<
  CustomSelectSearchAsyncFieldProps
> = ({
  label,
  className,
  onChange,
  onSelect,
  renderComponent,
  fetchDataFn,
  fetchDataLoading,
  fetchedData,
  requiredDataKey,
  presentationDataKey,
  ...props
}) => {
  const selectFieldRef = useRef<HTMLDivElement>(null);

  useOutsideClick(selectFieldRef, () => {
    setDidTouchSelectField(false);
    isUserSearchingRef.current = false;
  });

  const { handleBlur } = useFormikContext<any>();
  const [field, meta, helper] = useField(props);
  const [selectFieldValue, setSelectFieldValue] = useState<string>('');
  const [didTouchSelectField, setDidTouchSelectField] =
    useState<boolean>(false);
  const [optionData, setOptionData] = useState<any[]>(fetchedData ?? []);
  // const optionData = useRef<any[]>(fetchedData ?? []);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const selectedDataRef = useRef<Record<string, any> | null>(null);
  const isUserSearchingRef = useRef<boolean>(false);
  const hasInitiallyFetchedRef = useRef<boolean>(false);

  useEffect(() => {
    setOptionData(fetchedData ?? []);
  }, [fetchedData]);

  useEffect(() => {
    // Don't update display value if user is actively searching
    if (isUserSearchingRef.current) {
      return;
    }

    // If we have stored the selected data, use the presentation value
    if (selectedDataRef.current && presentationDataKey) {
      const displayValue = selectedDataRef.current[presentationDataKey] || '';
      setSelectFieldValue(displayValue);
    } else if (field.value) {
      // Try to find the matching item in optionData
      if (requiredDataKey && presentationDataKey && optionData.length > 0) {
        const matchedItem = optionData.find(
          (item) => item[requiredDataKey] === field.value,
        );
        if (matchedItem) {
          const displayValue = matchedItem[presentationDataKey];
          setSelectFieldValue(displayValue);
          selectedDataRef.current = matchedItem;
          return;
        }
      }
      // Fallback to field.value if no match found
      setSelectFieldValue(field.value ?? '');
    } else {
      setSelectFieldValue('');
      selectedDataRef.current = null;
    }
  }, [field.value, optionData, requiredDataKey, presentationDataKey]);

  const handleSelectFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    isUserSearchingRef.current = true;
    setSelectFieldValue(value);
    setDidTouchSelectField(true);

    // Clear previous timeout
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timeout
    debounceTimerRef.current = setTimeout(() => {
      if (fetchDataFn) {
        fetchDataFn(value);
      }
    }, 600);
  };

  const handleSelectFieldClick = (e: any) => {
    setDidTouchSelectField(true);

    // Fetch data on first click
    if (!hasInitiallyFetchedRef.current && fetchDataFn) {
      fetchDataFn(selectFieldValue || '');
      hasInitiallyFetchedRef.current = true;
    }
  };

  const handleOptionClick = (data: Record<string, any>) => {
    const requiredValue = requiredDataKey ? data[requiredDataKey] : data;
    const displayValue = presentationDataKey ? data[presentationDataKey] : data;

    // Store the selected data for future reference
    selectedDataRef.current = data;
    // User has selected an option, no longer actively searching
    isUserSearchingRef.current = false;

    helper.setValue(requiredValue);
    setSelectFieldValue(displayValue);
    setDidTouchSelectField(false);
    if (onChange) onChange(requiredValue);
    if (onSelect) onSelect(data);
  };

  const [state, dispatch] = useReducer(selectOptionsReducer, {
    index: -1,
  });

  const handleOptionStateChange = (index: any) => {
    dispatch({
      type: OptionReducerActionType.CLICKED_OPTION,
      payload: { index },
    });

    return handleOptionClick;
  };
  return (
    <InputWrapper className={className}>
      {label && (
        <InputLabel as='div' htmlFor={props.name || props.id}>
          {label}
        </InputLabel>
      )}
      <InputContainer ref={selectFieldRef}>
        <SelectFieldBox
          placeholder={props.placeholder ?? ''}
          type='text'
          onChange={handleSelectFieldChange}
          value={selectFieldValue}
          name={props.name}
          id={props.name}
          onBlur={handleBlur}
          onClick={handleSelectFieldClick}
          className={didTouchSelectField ? 'active' : ''}
        />

        {didTouchSelectField && (
          <SelectOptionBox>
            {optionData.length !== 0 ? (
              optionData.map((data: Record<string, any>, index: number) => {
                const isSelected = index === state.index ? true : false;
                return (
                  <SelectOption
                    key={index}
                    handleOptionClick={() => {
                      handleOptionClick(data);
                      handleOptionStateChange(index);
                    }}
                    isSelected={isSelected}
                  >
                    {renderComponent ? renderComponent(data) : <></>}
                  </SelectOption>
                );
              })
            ) : (
              <EmptyState>
                <em>{fetchDataLoading ? 'Loading...' : 'No Data'}</em>
              </EmptyState>
            )}
          </SelectOptionBox>
        )}
      </InputContainer>
      {meta.touched && meta.error ? (
        <FieldErrorInfo>{meta.error.capitalize()}</FieldErrorInfo>
      ) : null}
    </InputWrapper>
  );
};

export default CustomSelectSearchAsyncField;

interface SelectOptionProps {
  handleOptionClick: () => void;
  isSelected: boolean;
  children: any;
}

export const SelectOption: React.FC<SelectOptionProps> = ({
  handleOptionClick,
  isSelected,
  children,
}) => {
  return (
    <Option
      onClick={handleOptionClick}
      className={isSelected ? 'isSelected' : ''}
    >
      {children}
    </Option>
  );
};

const SelectFieldBox = styled(Input)`
  padding-right: 36px;
  &.active {
    border-color: ${({ theme }) => theme.palette.logiratePrimary};
  }
`;

export const InputContainer = styled.div`
  display: grid;
  height: fit-content;
  position: relative;
  height: fit-content;

  & > * {
    grid-column: 1;
    grid-row: 1;
  }
  &:hover {
    cursor: pointer;
  }
`;

export const SelectOptionBox = styled.div`
  position: absolute;
  top: 100%;
  width: 100%;
  max-height: 180px;
  display: flex;
  overflow: auto;
  flex-direction: column;
  gap: 4px;
  padding: 0;
  border: 1px solid ${({ theme }) => theme.palette.borderColor};
  box-shadow: 1px 4px 12px -1px rgba(44, 78, 39, 0.15);
  border-radius: 4px;
  background-color: white;
  margin-top: 4px;
  z-index: 10;
  color: ${({ theme }) => theme.palette.grayTextColor};

  /* border: 1px solid red; */
`;

export const Option = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  cursor: pointer;
  padding: 16px;
  font-size: 14px;

  &:hover {
    background-color: #f9fafb;
  }

  &:last-of-type {
    border: none;
  }

  &.isSelected {
    background-color: #f9fafb;
  }
`;

const EmptyState = styled.div`
  display: flex;
  padding: 16px;
  cursor: not-allowed;
  font-weight: 500;
  font-size: 14px;
  color: gray;
  align-items: center;
  justify-content: center;
  width: 100%;
`;
